import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHandshake,
  FaHotel,
  FaSpa,
  FaUtensils,
  FaMapMarkedAlt,
  FaCalendarCheck,
  FaShieldAlt,
  FaPercentage,
  FaChartLine,
  FaEnvelope,
  FaPrint,
  FaCheckCircle,
  FaStore,
  FaArrowRight,
} from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

const LOGO_URL =
  'https://xxgnzdljjqdnmucqdgvs.supabase.co/storage/v1/object/public/logo%20forfeoexplore/Logo%20forfeo%20explore.png';

type SendStatus = 'idle' | 'loading' | 'success' | 'error';

const benefits = [
  {
    icon: FaHandshake,
    title: 'Créer des Combos entre marchands',
    text: 'Un hôtel, un spa, un restaurant ou une activité peut inviter d’autres partenaires locaux à collaborer dans un Combo Forfeo.',
  },
  {
    icon: FaCalendarCheck,
    title: 'Plages horaires contrôlées',
    text: 'Les marchands proposent et confirment leurs disponibilités afin que le client puisse réserver une plage simple et claire.',
  },
  {
    icon: FaPercentage,
    title: 'Commission transparente',
    text: 'Forfeo applique une commission configurable. Le taux par défaut recommandé pour les Combos est de 8 %, visible avant acceptation.',
  },
  {
    icon: FaChartLine,
    title: 'Revenus suivis dans le dashboard',
    text: 'Chaque marchand peut suivre ses ventes, la commission Forfeo, son net estimé et les Combos actifs depuis son espace.',
  },
];

const comboExamples = [
  {
    name: 'Combo Détente',
    partners: 'Hôtel + spa + restaurant',
    value: 'Valeur : 350 $',
    price: 'Prix suggéré : 319 $',
    icon: FaSpa,
  },
  {
    name: 'Combo Découverte',
    partners: 'Activité locale + resto + boutique',
    value: 'Valeur : 185 $',
    price: 'Prix suggéré : 165 $',
    icon: FaMapMarkedAlt,
  },
  {
    name: 'Combo Week-end',
    partners: 'Hôtel + activité + expérience gourmande',
    value: 'Valeur : 520 $',
    price: 'Prix suggéré : 469 $',
    icon: FaHotel,
  },
  {
    name: 'Combo Saveurs',
    partners: 'Restaurant + micro-expérience + dégustation',
    value: 'Valeur : 140 $',
    price: 'Prix suggéré : 125 $',
    icon: FaUtensils,
  },
];

const legalPoints = [
  'Chaque marchand demeure responsable de sa portion de service, de ses disponibilités, de ses permis, de ses assurances et de la qualité de sa prestation.',
  'Forfeo agit comme plateforme technologique de visibilité, de coordination commerciale, de réservation et de paiement.',
  'Chaque acceptation de Combo est horodatée avec le prix, le taux de commission et la version des conditions acceptées.',
  'Forfeo peut suspendre un Combo en cas de litige, d’indisponibilité ou de non-respect des conditions de participation.',
];

export default function PartnerCombosBrochure() {
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [status, setStatus] = useState<SendStatus>('idle');
  const [message, setMessage] = useState('');

  const printableDate = useMemo(() => new Date().toLocaleDateString('fr-CA'), []);

  const sendBrochure = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('partner-materials', {
        body: {
          action: 'send_combo_brochure',
          email,
          business_name: businessName,
          source: 'partner_combos_brochure',
        },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'send_failed');

      setStatus('success');
      setMessage('Le feuillet a été envoyé par courriel.');
    } catch (error: any) {
      setStatus('error');
      setMessage(error?.message || 'Impossible d’envoyer le feuillet pour le moment.');
    }
  };

  const printBrochure = async () => {
    try {
      await supabase.functions.invoke('partner-materials', {
        body: { action: 'log_download', source: 'partner_combos_brochure_print' },
      });
    } catch {
      // Never block printing because of tracking.
    }
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <section className="relative overflow-hidden border-b border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.28),transparent_35%),radial-gradient(circle_at_top_right,rgba(251,191,36,0.18),transparent_28%),linear-gradient(135deg,#020617,#0f172a_52%,#111827)]">
        <div className="absolute inset-0 opacity-30" aria-hidden="true">
          <div className="absolute left-8 top-10 h-72 w-72 rounded-full bg-cyan-400 blur-3xl" />
          <div className="absolute bottom-0 right-10 h-80 w-80 rounded-full bg-amber-400 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-cyan-100 shadow-2xl backdrop-blur">
                <img src={LOGO_URL} alt="Forfeo Explore" className="h-8 w-auto rounded bg-white px-2 py-1" />
                Feuillet partenaire · Combos collaboratifs
              </div>

              <h1 className="text-4xl font-black tracking-tight sm:text-6xl">
                Créez des <span className="text-cyan-300">Combos Forfeo</span> avec les meilleurs partenaires locaux.
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-200">
                Les Combos permettent aux marchands de s’unir pour vendre des expériences plus fortes : hôtel, spa, restaurant, activité, boutique locale ou découverte régionale. Chaque partenaire accepte la collaboration, le prix et ses disponibilités avant publication.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/merchant/combos"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-300 px-6 py-3 font-black text-slate-950 shadow-xl shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:bg-white"
                >
                  Créer un Combo <FaArrowRight />
                </Link>
                <button
                  onClick={printBrochure}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 font-black text-white transition hover:bg-white/20"
                >
                  <FaPrint /> Imprimer / PDF
                </button>
              </div>
            </div>

            <div className="w-full max-w-md rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur print:hidden">
              <div className="mb-5 rounded-3xl bg-white p-5 text-slate-950 shadow-xl">
                <div className="text-xs font-black uppercase tracking-[0.25em] text-cyan-700">Exemple rapide</div>
                <h2 className="mt-2 text-2xl font-black">Combo Détente Québec</h2>
                <p className="mt-2 text-sm text-slate-600">Hôtel + spa + restaurant</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-slate-100 p-3">
                    <div className="font-black text-slate-900">Valeur</div>
                    <div className="text-slate-600">350 $</div>
                  </div>
                  <div className="rounded-2xl bg-cyan-50 p-3">
                    <div className="font-black text-cyan-800">Prix suggéré</div>
                    <div className="text-cyan-700">319 $</div>
                  </div>
                  <div className="rounded-2xl bg-amber-50 p-3">
                    <div className="font-black text-amber-800">Commission</div>
                    <div className="text-amber-700">8 %</div>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-3">
                    <div className="font-black text-emerald-800">Publication</div>
                    <div className="text-emerald-700">auto après acceptation</div>
                  </div>
                </div>
              </div>

              <form onSubmit={sendBrochure} className="space-y-3">
                <label className="block text-sm font-bold text-slate-100">Recevoir le feuillet par courriel</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Nom de l’entreprise"
                  className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-slate-950 outline-none ring-cyan-300 transition focus:ring-4"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="courriel@entreprise.com"
                  required
                  className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-slate-950 outline-none ring-cyan-300 transition focus:ring-4"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 font-black text-slate-950 transition hover:bg-cyan-100 disabled:opacity-60"
                >
                  <FaEnvelope /> {status === 'loading' ? 'Envoi...' : 'Recevoir le feuillet'}
                </button>
                {message && (
                  <p className={`text-sm font-bold ${status === 'success' ? 'text-emerald-200' : 'text-rose-200'}`}>{message}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <section className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="rounded-[1.75rem] border border-white/10 bg-white/[0.06] p-6 shadow-xl">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-300 text-slate-950">
                  <Icon />
                </div>
                <h3 className="text-lg font-black">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.text}</p>
              </article>
            );
          })}
        </section>

        <section className="mt-16 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-7 shadow-2xl">
            <div className="mb-6 flex items-center gap-3">
              <FaStore className="text-cyan-300" />
              <h2 className="text-2xl font-black">Comment fonctionne un Combo?</h2>
            </div>
            <ol className="space-y-4 text-slate-200">
              {[
                'Un marchand crée une proposition de Combo et invite 1 ou 2 partenaires complémentaires.',
                'Forfeo calcule une suggestion de prix avec la commission applicable et un net estimé pour les partenaires.',
                'Chaque marchand invité accepte ou refuse la collaboration, le prix, sa portion et les plages horaires proposées.',
                'Quand les conditions sont remplies, le Combo devient visible sur Forfeo Explore et prêt à être vendu via Forfeo.com.',
                'Le client choisit une plage horaire simple, puis reçoit une confirmation claire avec les étapes du Combo.',
              ].map((step, index) => (
                <li key={step} className="flex gap-4 rounded-3xl bg-slate-900/70 p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-300 text-sm font-black text-slate-950">{index + 1}</span>
                  <span className="leading-7">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white p-7 text-slate-950 shadow-2xl">
            <div className="text-xs font-black uppercase tracking-[0.25em] text-cyan-700">Exemples de Combos</div>
            <div className="mt-5 grid gap-4">
              {comboExamples.map((combo) => {
                const Icon = combo.icon;
                return (
                  <article key={combo.name} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-cyan-300">
                        <Icon />
                      </div>
                      <div>
                        <h3 className="font-black">{combo.name}</h3>
                        <p className="text-sm text-slate-600">{combo.partners}</p>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs font-black">
                          <span className="rounded-full bg-white px-3 py-1 text-slate-700 ring-1 ring-slate-200">{combo.value}</span>
                          <span className="rounded-full bg-cyan-100 px-3 py-1 text-cyan-800">{combo.price}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mt-16 rounded-[2rem] border border-amber-300/20 bg-amber-300/10 p-7 shadow-2xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-3 text-amber-200">
                <FaShieldAlt />
                <span className="text-sm font-black uppercase tracking-[0.25em]">Protection Forfeo</span>
              </div>
              <h2 className="text-2xl font-black">Une collaboration claire, acceptée et horodatée.</h2>
              <p className="mt-3 max-w-3xl leading-7 text-slate-200">
                Forfeo protège la plateforme, les clients et les marchands avec une acceptation explicite des conditions de participation, du prix, de la commission et des responsabilités propres à chaque partenaire.
              </p>
            </div>
            <div className="rounded-3xl bg-white p-4 text-sm font-black text-slate-950 shadow-xl print:hidden">
              Version du feuillet : {printableDate}
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {legalPoints.map((point) => (
              <div key={point} className="flex gap-3 rounded-3xl bg-slate-950/60 p-4 text-sm leading-6 text-slate-200">
                <FaCheckCircle className="mt-1 shrink-0 text-emerald-300" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
